import { ApolloProvider, useQuery } from '@apollo/client';
import { Button } from '@mui/material';
import { SearchkitClient, SearchkitProvider } from '@searchkit/client';
import { cloneDeep } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '../../common/auth/UserContext';
import { usePersistedSearchkitVariables } from '../../common/components/filtering/filters';
import Loading from '../../common/components/Loading';
import { FETCH_POLICY_CONFIG } from '../../common/constants/constants';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import FormModal from '../../common/form/FormModal';
import I18n, { useI18n } from '../../common/i18n/I18n';
import { V } from '../../common/Layout';
import DataList from '../../common/list/DataList';
import ListView from '../../common/list/ListView';
import { searchClient, slimResultsQuery } from '../../common/list/slimQuery';
import { EMPTY_DATERANGE } from '../../lab/form/LabForms';
import { KEY_DATES_ACTIVITY_TYPE } from '../../supply/opportunity/common/opportunityConstants';
import { filterActivitiesByQuery, handleToggleActivity } from '../../supply/opportunity/common/opportunityUtils';
import SearchPanel from '../../supply/searchkit/facets/SearchPanel';
import { ActivityFormType, EMPTY_ACTIVITY, getKeyActivitiesRefs } from '../helpers/activityUtils';
import { ProductionTypeSuperset, useSaveKeyActivities } from '../helpers/productionApi';
import { useProductionActivityData } from '../hooks/saveActivityData';
import SearchOrCreateResult from '../SearchOrCreateResult';
import ActivityForm from './ActivityForm';
import ActivityListTile from './ActivityListTile';

const List = ({
    id,
    loading,
    data,
    entity,
    feature,
    productionId,
    planId,
    setSelectedActivity,
    setIsCreate,
    setPrepopulatedActivity,
    viewStateStableRef,
    setViewState,
    ...props
}) => {
    const { saveKeyActivities } = useSaveKeyActivities();
    const { activeOrganizationAccount } = useContext(UserContext);
    const isButtonLoading = useRef({ id: '', isLoading: false });
    const { variables } = usePersistedSearchkitVariables(id);

    //need to add reference field for activities in production query on service
    const { data: activityData } = useQuery(slimResultsQuery('Activity'), {
        variables: {
            filters: [
                {
                    identifier: 'entity',
                    value: 'Activity'
                },
                {
                    identifier: 'production',
                    value: productionId
                }
            ],
            page: { size: 1000, from: 0 }
        },
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        client: searchClient,
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        notifyOnNetworkStatusChange: true,
        skip: !productionId
    });

    const keyActivities = useMemo(() => {
        if (viewStateStableRef.current?.plans?.[0]?.keyActivities) {
            return viewStateStableRef.current.plans[0].keyActivities.map((activity) => activity.id);
        }
        return [];
    }, [viewStateStableRef.current?.plans?.[0].keyActivities]);

    const dataWithKeyDates = useMemo(() => {
        if (data?.results?.hits?.items) {
            return data?.results?.hits?.items.map((item) => {
                return { ...item, isKeyDate: keyActivities.includes(item.id) };
            });
        } else return [];
    }, [data?.results?.hits?.items, keyActivities]);

    return !loading ? (
        dataWithKeyDates && viewStateStableRef.current && (
            <DataList
                {...props}
                id={id}
                feature={feature}
                pagination={data?.results?.hits?.page}
                items={dataWithKeyDates}
                emptyListElement={
                    <Button
                        onClick={() => {
                            setIsCreate(true);
                            setPrepopulatedActivity({ ...EMPTY_ACTIVITY, name: variables.query });
                        }}
                    >
                        <I18n token="form.activity.createNewActivity" />
                    </Button>
                }
                ItemRenderer={(props) => {
                    return (
                        <ActivityListTile
                            {...props}
                            isLoading={isButtonLoading}
                            markActiivtyId={(activityId) => {
                                isButtonLoading.current = { id: activityId, isLoading: true };
                                setViewState((prevVal) => {
                                    return {
                                        ...prevVal,
                                        plans: [
                                            {
                                                ...viewStateStableRef.current.plans[0],
                                                keyActivities: getKeyActivitiesRefs(
                                                    activityId,
                                                    keyActivities,
                                                    activityData?.results?.hits?.items,
                                                    true
                                                )
                                            }
                                        ]
                                    } as ProductionTypeSuperset;
                                });
                                saveKeyActivities(
                                    planId,
                                    getKeyActivitiesRefs(
                                        activityId,
                                        keyActivities,
                                        activityData?.results?.hits?.items,
                                        true
                                    ),
                                    true
                                ).then(() => (isButtonLoading.current = { id: '', isLoading: false }));
                            }}
                            unmarkActivityId={(activityId) => {
                                isButtonLoading.current = { id: activityId, isLoading: true };
                                setViewState((prevVal) => {
                                    return {
                                        ...prevVal,
                                        plans: [
                                            {
                                                ...viewStateStableRef.current.plans[0],
                                                keyActivities: getKeyActivitiesRefs(
                                                    activityId,
                                                    keyActivities,
                                                    activityData?.results?.hits?.items,
                                                    false
                                                )
                                            }
                                        ]
                                    } as ProductionTypeSuperset;
                                });
                                saveKeyActivities(
                                    planId,
                                    getKeyActivitiesRefs(
                                        activityId,
                                        keyActivities,
                                        activityData?.results?.hits?.items,
                                        false
                                    ),
                                    true
                                ).then(() => (isButtonLoading.current = { id: '', isLoading: false }));
                            }}
                            isDisabled={isButtonLoading.current.isLoading}
                        />
                    );
                }}
                disableRipple={true}
                onClick={(item) => {
                    !isButtonLoading.current.isLoading && setSelectedActivity(item.id);
                }}
            />
        )
    ) : (
        <Loading />
    );
};

export default ({
    open,
    onClose,
    planId = undefined,
    id = undefined,
    prefilters = [],
    productionId = undefined,
    activityId = undefined,
    setViewState,
    viewState
}) => {
    const searchkit = new SearchkitClient();
    const [selectedActivity, setSelectedActivity] = useState('');
    const [prepopulatedActivity, setPrepopulatedActivity] = useState<ActivityFormType>(EMPTY_ACTIVITY);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isCreate, setIsCreate] = useState(false);
    const [query, setQuery] = useState('');
    const [keyDatesState, setKeyDatesState] = useState({});
    const [searchAllDates, setSearchAllDates] = useState(false);
    const [pendingAction, setPendingAction] = useState<{ activityId: string; type: string } | null>(null);

    const { saveKeyActivities } = useSaveKeyActivities();
    const { activeOrganizationAccount } = useContext(UserContext);
    const { activityData, loading, refetch: refetchActivityData } = useProductionActivityData(productionId, activeOrganizationAccount);

    const isButtonLoadingRef = useRef<{ id: string; isLoading: boolean }>({ id: '', isLoading: false });
    const activityFormRef = useRef(null);
    const viewStateSableRef = useRef(viewState);

    const allActivities = useMemo(() => {
        const activities = viewState?.plans?.[0]?.activities || [];
        const keyActivityIds = new Set((viewState?.plans?.[0]?.keyActivities || []).map((a) => a.id));

        return activities.map((a) => ({
            ...a,
            id: a.identity?.id || a.id,
            isKeyDate: keyActivityIds.has(a.identity?.id || a.id)
        }));
    }, [viewState?.plans]);

    const filteredDatesList = useMemo(() => filterActivitiesByQuery(allActivities, query), [allActivities, query]);

    const keyActivities = useMemo(() => {
        return (viewState?.plans?.[0]?.keyActivities || []).map((activity) => activity.id);
    }, [viewState?.plans?.[0]?.keyActivities]);

    const toggleActivity = useCallback(
        handleToggleActivity({
            viewStateSableRef,
            setViewState,
            saveKeyActivities,
            planId,
            isButtonLoadingRef,
            getKeyActivitiesRefs,
            activityData,
            keyActivities
        }),
        [
            viewStateSableRef,
            setViewState,
            saveKeyActivities,
            planId,
            isButtonLoadingRef,
            getKeyActivitiesRefs,
            activityData,
            keyActivities
        ]
    );

    useEffect(() => {
        if (open) {
            setQuery('');
            setSearchAllDates(false);
        }
    }, [open]);

    useEffect(() => {
        if (query && isCreate) {
            setPrepopulatedActivity({ ...EMPTY_ACTIVITY, ...cloneDeep(EMPTY_DATERANGE), name: query });
        }
    }, [query, isCreate]);

    useEffect(() => {
        setSearchAllDates(false);
    }, [query]);

    useEffect(() => {
        if (Array.isArray(filteredDatesList) && filteredDatesList.length === 0 && query) {
            setSearchAllDates(true);
        }
    }, [filteredDatesList, query]);

    useEffect(() => {
        viewStateSableRef.current = viewState;
    }, [viewState]);

    useEffect(() => {
        if (activityId) {
            setSelectedActivity(activityId);
        }
    }, [activityId]);

    const createSafeActivityHandler = (type: string) => {
        return (activityId: string) => {
            isButtonLoadingRef.current = { id: activityId, isLoading: true };

            if (!activityData) {
                setPendingAction({ activityId, type });
                return;
            }

            toggleActivity(activityId, type).finally(() => {
                isButtonLoadingRef.current = { id: '', isLoading: false };
            });
        };
    };

    useEffect(() => {
        if (pendingAction && activityData) {
            const { activityId, type } = pendingAction;

            toggleActivity(activityId, type).finally(() => {
                isButtonLoadingRef.current = { id: '', isLoading: false };
                setPendingAction(null);
            });
        }
    }, [activityData, pendingAction]);

    return (
        <FormModal
            width={'580px'}
            isOpen={open}
            onClose={() => {
                setSelectedActivity('');
                setIsCreate(false);

                onClose();
            }}
            title={<I18n token="form.activity.addKeyDate" />}
            id={`keyDates`}
            hideButtons={!(selectedActivity || isCreate)}
            hideDefaultButtons={true}
            additionalActions={[
                {
                    label: useI18n('dialog.cancel'),
                    onClick: () => {
                        setSelectedActivity(undefined);
                        setIsCreate(false);
                    },
                    variant: 'text'
                },
                {
                    label: useI18n('dialog.save'),
                    onClick: () => {
                        activityFormRef.current.handleSave().then(() => {
                            setQuery('');
                            setIsCreate(false);
                            setSelectedActivity('');
                        });
                    },
                    variant: 'contained',
                    disabled: isSaveDisabled || isRefreshing,
                    loading: isSaveDisabled || isRefreshing
                }
            ]}
        >
            {selectedActivity || isCreate ? (
                <ActivityForm
                    ref={activityFormRef}
                    isInModal={false}
                    showBasic={true}
                    onClose={() => {
                        setSelectedActivity('');
                        setIsCreate(false);
                    }}
                    isResourceForm
                    planId={planId}
                    productionId={productionId}
                    activityId={selectedActivity}
                    setActivityId={setSelectedActivity}
                    prepopulatedActivity={prepopulatedActivity}
                    setPrepopulatedActivity={setPrepopulatedActivity}
                    isCreate={isCreate}
                    setIsSaveDisabled={setIsSaveDisabled}
                    setIsRefreshing={setIsRefreshing}
                    refetchActivityData={refetchActivityData}
                />
            ) : (
                <>
                    <SearchPanel
                        id={'key-dates-modal-search-people'}
                        query={query}
                        setQuery={setQuery}
                        loading={false}
                        sx={{ width: '100%', p: 1 }}
                        searchPlaceholder={useI18n('form.activity.search.existing.keyDate')}
                        mobile={false}
                    />
                    <V
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            pb: 2,
                            width: '100%',
                            height: '100%',
                            overflow: 'auto'
                        }}
                    >
                        {!searchAllDates && (
                            <DataList
                                items={filteredDatesList}
                                onClick={undefined}
                                disableRipple
                                ItemRenderer={(props) => {
                                    const combinedLoadingRef = {
                                        current: {
                                            id: isButtonLoadingRef.current?.id || '',
                                            isLoading: isButtonLoadingRef.current?.isLoading || loading
                                        }
                                    };

                                    return (
                                        <ActivityListTile
                                            {...props}
                                            isLoading={combinedLoadingRef}
                                            isDisabled={isButtonLoadingRef.current?.isLoading}
                                            markActiivtyId={createSafeActivityHandler(KEY_DATES_ACTIVITY_TYPE.mark)}
                                            unmarkActivityId={createSafeActivityHandler(KEY_DATES_ACTIVITY_TYPE.unmark)}
                                        />
                                    );
                                }}
                            />
                        )}

                        {searchAllDates && (
                            <ApolloProvider client={searchClient}>
                                <SearchkitProvider client={searchkit}>
                                    <ListView
                                        key={query}
                                        id={id}
                                        entity={ENTITY_CONSTANTS.activity}
                                        feature="Activity"
                                        uri={undefined}
                                        query={slimResultsQuery('Activity')}
                                        initialSearchQuery={query}
                                        enableSearch={false}
                                        enableSearchLoader
                                        preFilters={prefilters}
                                        immutableFilters={[{ identifier: 'production', value: productionId }]}
                                        showBreadCrumbs={false}
                                        defaultSortModel={{ field: 'start', sort: 'asc' }}
                                        containerSx={{ height: '100%', m: 0, p: 1 }}
                                        facetsVisibility={{
                                            filters: false,
                                            searchPanel: false,
                                            promotedFacets: false,
                                            quickFilter: false
                                        }}
                                        searchPlaceholder={useI18n('activity.search.placeholder')}
                                    />
                                </SearchkitProvider>
                            </ApolloProvider>
                        )}
                    </V>
                </>
            )}
            {query && !isCreate && (
                <SearchOrCreateResult
                    query={query}
                    setAllSearch={setSearchAllDates}
                    setIsCreate={setIsCreate}
                    setState={setKeyDatesState}
                    state={keyDatesState}
                    type={ENTITY_CONSTANTS.activity}
                    isSearchedInDirectory={searchAllDates}
                />
            )}
        </FormModal>
    );
};
